import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { timeAgo, initials } from "@/lib/format";
import { Bell, Check } from "@phosphor-icons/react";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const btnRef = useRef(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/notifications", { params: { limit: 20 } })
      .then((r) => { setItems(r.data.items || []); setUnread(r.data.unread_count || 0); })
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (!btnRef.current?.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const openNote = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    navigate(`/tickets/${n.ticket_id}`);
    load();
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch {}
  };

  return (
    <div className="relative" ref={btnRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors"
        title="Notifications"
      >
        <Bell size={18} weight={unread > 0 ? "fill" : "regular"} className={unread > 0 ? "text-[#39A36E]" : ""} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-[#39A36E] text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 top-full mt-2 w-96 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-heading font-semibold text-sm">Notifications</div>
              <div className="text-[11px] text-slate-500">{unread} unread</div>
            </div>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-[11px] text-[#39A36E] hover:text-[#2E8C5D] flex items-center gap-1"
                data-testid="notification-mark-all"
              >
                <Check size={11} weight="bold" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">
                <Bell size={22} className="mx-auto mb-2 text-slate-300" />
                No notifications yet.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openNote(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors flex items-start gap-3 ${!n.is_read ? "bg-[#39A36E]/5" : ""}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium shrink-0">
                    {initials(n.actor_name || "?")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-800">{n.text}</div>
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5">{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.is_read && <div className="w-2 h-2 rounded-full bg-[#39A36E] shrink-0 mt-2" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
